/**
 * The support tab: every conversation with the desk, and the one that is open.
 *
 * From `lg` up the two sit side by side, the way a mail client draws them: the
 * list stays put and the conversation changes beside it, so moving between two
 * tickets is one click instead of "back, find, open". Under `lg` there is room
 * for one of them at a time, and the same markup shows the list OR the
 * conversation.
 *
 * Which conversation is open is URL state (`?ticket=<id>`), and so is the "new
 * request" dialog (`?new=1`) - the front page links straight to both, and a
 * reload lands where the reader was.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, LifeBuoy, MessagesSquare, Plus, RefreshCw } from "lucide-react";
import { Button, Card, cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { portal, type PortalTicket } from "../../../lib/portal";
import { keepPreview } from "../../../lib/previewMode";
import { EmptyState, ListSkeleton, PageHead } from "../../ui";
import { NewTicketDialog } from "./NewTicketDialog";
import { TicketList } from "./TicketList";
import { TicketThread } from "./TicketThread";

export function SupportSection() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const ticketId = params.get("ticket");
  const creating = params.get("new") === "1";
  const [tickets, setTickets] = useState<PortalTicket[] | null>(null);
  const [failed, setFailed] = useState(false);

  function load() {
    setFailed(false);
    void portal.tickets().then(setTickets).catch(() => setFailed(true));
  }
  useEffect(load, []);

  // Every rewrite goes through `keepPreview`: `setParams` replaces the whole
  // query string, and a staff preview would otherwise drop its token.
  const here = (extra: Record<string, string> = {}) =>
    keepPreview({ tab: "support", ...(ticketId ? { ticket: ticketId } : {}), ...extra });
  const open = (id: string) => setParams(keepPreview({ tab: "support", ticket: id }));
  const back = () => setParams(keepPreview({ tab: "support" }));
  const startNew = () => setParams(here({ new: "1" }));
  // `replace`, so Back does not reopen a dialog the reader just closed.
  const closeNew = () => setParams(here(), { replace: true });

  const newButton = (
    <Button onClick={startNew}>
      <Plus />
      {t("portal.support.new")}
    </Button>
  );
  const none = tickets !== null && tickets.length === 0 && !ticketId;

  return (
    <div>
      {/* Under `lg` an open conversation takes the whole screen, title and
          all; its own Back button is the way to the list. */}
      <div className={cn(ticketId && "max-lg:hidden")}>
        <PageHead
          title={t("portal.support.title")}
          description={t("portal.support.lead")}
          actions={none ? undefined : newButton}
        />
      </div>

      {failed ? (
        <Card>
          <EmptyState
            icon={AlertCircle}
            title={t("portal.loadFailed")}
            action={
              <Button variant="outline" onClick={load}>
                <RefreshCw />
                {t("portal.retry")}
              </Button>
            }
          />
        </Card>
      ) : none ? (
        <Card>
          <EmptyState
            icon={LifeBuoy}
            title={t("portal.support.emptyTitle")}
            body={t("portal.support.emptyBody")}
            action={newButton}
          />
        </Card>
      ) : (
        <Card className="overflow-hidden lg:grid lg:min-h-[30rem] lg:grid-cols-[320px_minmax(0,1fr)]">
          <div className={cn("min-w-0 lg:border-r lg:border-border", ticketId && "max-lg:hidden")}>
            {tickets ? (
              <TicketList tickets={tickets} activeId={ticketId} onOpen={open} />
            ) : (
              <ListSkeleton rows={4} label={t("common.loading")} />
            )}
          </div>
          <div className={cn("flex min-w-0 flex-col", !ticketId && "max-lg:hidden")}>
            {ticketId ? (
              // Keyed by id so switching conversations remounts rather than
              // showing the previous one's messages under the new subject.
              <TicketThread key={ticketId} id={ticketId} onBack={back} onChanged={load} />
            ) : (
              <div className="flex flex-1 items-center justify-center">
                <EmptyState
                  icon={MessagesSquare}
                  title={t("portal.support.pickTitle")}
                  body={t("portal.support.pickBody")}
                />
              </div>
            )}
          </div>
        </Card>
      )}

      <NewTicketDialog
        open={creating}
        onClose={closeNew}
        onCreated={(id) => {
          load();
          setParams(keepPreview({ tab: "support", ticket: id }), { replace: true });
        }}
      />
    </div>
  );
}
